/**
 * userScopedStorage
 *
 * Personal data in localStorage (recently-viewed docs, pinned docs,
 * favourite workspaces, active org/personal scope, dismissed
 * announcements) MUST be keyed by user id, otherwise two accounts
 * signing in on the same browser overwrite each other's data and
 * the second account sees the first's history on the home page.
 *
 * This helper wraps localStorage with a `.${userId}` suffix and
 * one-time migrates any legacy unkeyed value into the current
 * user's slot on first read.
 *
 *   getUserScopedJSON('pidly.recentDocs', [])
 *     ↳ reads localStorage['pidly.recentDocs.<userId>'], JSON-parses,
 *       falls back to the second arg. First call also migrates any
 *       legacy localStorage['pidly.recentDocs'] into the keyed slot
 *       and removes the legacy entry.
 *
 *   setUserScopedJSON('pidly.recentDocs', list)
 *     ↳ writes JSON-stringified value to the keyed slot.
 *
 *   getUserScopedString / setUserScopedString
 *     ↳ same shape, raw string values (e.g. activeScope is 'personal'
 *       or an orgId).
 *
 *   removeUserScoped('pidly.recentDocs')
 *     ↳ removes the current user's slot only.
 *
 *   wipePersonalLocalStorage()
 *     ↳ on logout. Removes ALL keys listed in PERSONAL_KEYS across
 *       every user slot AND the legacy unkeyed slots. UI prefs
 *       (collapsed states, zoom levels, etc.) are intentionally NOT
 *       wiped — they're not personal data, just per-browser
 *       preferences.
 */

import { getStoredUser } from './authStorage';
import { rawRemoveItem } from './rawStorage';

// Keys that hold per-user personal data. Add to this list when a new
// personal-data localStorage key is introduced anywhere in the app.
export const PERSONAL_KEYS = [
  'pidly.recentDocs',
  'pidly.pinnedDocs',
  'pidly.favDocs',            // FileSidebar ⋯ → Favourite (pinned band)
  'pidly.favWorkspaces',
  'pidly.favouriteProjects',  // pinFavStorage: favourite workspaces (inner projects)
  'pidly.recentWorkspaces',   // pinFavStorage: recently-opened workspaces
  'pidly.pinnedOrgs',         // favourite top-level projects (orgs)
  'pidly.activeScope',
  'pidly.readAnnouncements',
  'pidly.regionFilter',
  'pidly.bulkInheritCandidates',
  'pidly.theme',
  'pidly.themeOverrides',
  'pidly.workspaceDefaultView',
];

function currentUserId() {
  try {
    const u = getStoredUser();
    return u?.id || 'anon';
  } catch {
    return 'anon';
  }
}

/** The signed-in user's own slot for `baseKey` (`<baseKey>.<userId>`). */
export function scopedKey(baseKey) {
  return `${baseKey}.${currentUserId()}`;
}

// One-time migration: if the keyed slot is empty but the legacy unkeyed
// key has a value, move it to the current user's slot and remove the
// legacy entry. Only runs when no user is signed in OR the keyed slot
// for the signed-in user is empty — never overwrites existing data.
function migrateLegacy(baseKey) {
  try {
    const keyed = scopedKey(baseKey);
    if (localStorage.getItem(keyed) !== null) return; // already migrated
    const legacy = localStorage.getItem(baseKey);
    if (legacy === null) return;
    localStorage.setItem(keyed, legacy);
    localStorage.removeItem(baseKey);
  } catch {
    /* localStorage unavailable */
  }
}

export function getUserScopedString(baseKey, fallback = null) {
  migrateLegacy(baseKey);
  try {
    const v = localStorage.getItem(scopedKey(baseKey));
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

export function setUserScopedString(baseKey, value) {
  try { localStorage.setItem(scopedKey(baseKey), value); } catch {}
}

export function getUserScopedJSON(baseKey, fallback) {
  migrateLegacy(baseKey);
  try {
    const raw = localStorage.getItem(scopedKey(baseKey));
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function setUserScopedJSON(baseKey, value) {
  try { localStorage.setItem(scopedKey(baseKey), JSON.stringify(value)); } catch {}
}

export function removeUserScoped(baseKey) {
  try { localStorage.removeItem(scopedKey(baseKey)); } catch {}
}

// Clears personal data for ALL users on this browser plus any legacy
// unkeyed values. Call from the logout handler.
//
// Uses rawRemoveItem, NOT localStorage.removeItem: preferencesSync
// patches removeItem to mirror deletions to the account, so the plain
// call pushed `null` for every wiped key and destroyed the user's
// server-side preferences — their theme included — on every logout.
// This wipe is browser hygiene; the account's copy must survive it.
export function wipePersonalLocalStorage() {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      for (const base of PERSONAL_KEYS) {
        if (k === base || k.startsWith(`${base}.`)) {
          toRemove.push(k);
          break;
        }
      }
    }
    for (const k of toRemove) rawRemoveItem(k);
  } catch {
    /* localStorage unavailable */
  }
}
